import { checkIfFileExists } from '@hashnode/file-parsing'
import path from 'path'
import { ParsedPageWithContent, ParsedSidebarItemConfigWithPageContent } from '../schemas'
import { MissingFile, RelativeLink } from '../types'

/**
 * Regular expression to match local file references in MDX or MD content. [description](path/to/file.md)
 */
const MD_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g

/**
 * Replaces local file references in MDX or MD content with relative links including the guide slug.
 * @param content - The MDX or MD content as a string.
 * @param localFileMap - A map of local file references to their corresponding paths.
 * @param basePath - The base path of the guide used to resolve the relative paths. Normally the guide slug.
 * @returns The content with local file references replaced by relative links including the guide slug.
 */
export function replaceLocalReferences({
	content,
	localFileMap,
	basePath,
}: {
	content: string
	localFileMap: Record<string, string>
	basePath: string
}): string {
	return content.replace(MD_LINK_REGEX, (match, linkText: string, linkPath: string) => {
		const [filePath, anchor] = linkPath.split('#')
		const path = localFileMap[filePath]
		if (path) {
			// Include the guide slug so that we can use relative URLs
			// Lowercase the anchor so that the id's match with the autogenerated TOC id's
			const fullPath = `/${basePath}${path}${anchor ? '#' + anchor.toLowerCase() : ''}`
			return `[${linkText}](${fullPath})`
		}
		return match
	})
}

/**
 * Checks if the referenced file exists.
 * @param content - The markdown content as a string.
 * @param folderPath - The path of the folder where the file exists used to resolve the relative paths.
 * @param numberOfMatterLines - The number of lines in the frontmatter.
 * @returns An array of missing files with their reference line numbers.
 */
export function checkReferencedFiles({
	content,
	folderPath,
	numberOfMatterLines,
}: {
	content: string
	folderPath: string
	numberOfMatterLines: number
}): MissingFile[] {
	const missingFiles: MissingFile[] = []
	const lines = content.split('\n')

	lines.forEach((line, lineIndex) => {
		line.replace(MD_LINK_REGEX, (match, _, linkPath) => {
			if (linkPath.startsWith('http://') || linkPath.startsWith('https://')) {
				return match
			}

			const [filePath] = linkPath.split('#')

			if (!filePath.endsWith('.md') && !filePath.endsWith('.mdx')) {
				return match
			}

			const absolutePath = path.join(folderPath, filePath)

			if (!checkIfFileExists(absolutePath)) {
				missingFiles.push({
					missingFilePath: absolutePath,
					referenceLineNumber: lineIndex + 1 + numberOfMatterLines,
				})
			}
			return match
		})
	})

	return missingFiles
}

/**
 * Replaces all relative file path links in markdown content with absolute ones.
 * @param content - The markdown content as a string.
 * @param folderPath - The path of the folder where the file exists used to resolve the relative paths.
 * @returns The content with relative file path links replaced by absolute ones.
 * @throws Error if a referenced file is not found.
 */
export function replaceRelativeLocalPathsWithAbsoluteLocalPaths({
	content,
	folderPath,
}: {
	content: string
	folderPath: string
}): string {
	return content.replace(MD_LINK_REGEX, (match, linkText, linkPath) => {
		if (linkPath.startsWith('http://') || linkPath.startsWith('https://')) {
			return match
		}

		const [filePath, anchor] = linkPath.split('#')

		if (!filePath.endsWith('.md') && !filePath.endsWith('.mdx')) {
			return match
		}

		const absolutePath = path.join(folderPath, filePath)

		if (!checkIfFileExists(absolutePath)) {
			throw new Error(`Referenced file not found: ${absolutePath}`)
		}

		return `[${linkText}](${absolutePath}${anchor ? '#' + anchor : ''})`
	})
}

/**
 * Checks for relative links in markdown content.
 * @param content - The markdown content as a string.
 * @param folderPath - The path of the folder where the file exists (not used in this function).
 * @returns An object containing an array of relative links with their line numbers.
 */
export function checkForRelativeLinks({
	content,
	numberOfMatterLines,
}: {
	content: string
	numberOfMatterLines: number
}): RelativeLink[] {
	const relativeLinks: RelativeLink[] = []

	const lines = content.split('\n')
	lines.forEach((line, index) => {
		line.replace(MD_LINK_REGEX, (match, _, linkPath) => {
			// Ignore http links, links with file extensions, and same-file anchors
			if (
				!linkPath.startsWith('http://') &&
				!linkPath.startsWith('https://') &&
				!linkPath.match(/\.[a-zA-Z0-9]+$/) &&
				!linkPath.startsWith('#')
			) {
				// Check if the link is relative
				if (linkPath.startsWith('..') || linkPath.startsWith('/') || !linkPath.includes('/')) {
					relativeLinks.push({
						link: linkPath,
						referenceLineNumber: index + 1 + numberOfMatterLines,
					})
				}
			}
			return match // Return the original match to avoid modifying the content
		})
	})

	return relativeLinks
}

/**
 * Creates a map of file paths to full slugs for all pages including hidden pages or nested sidebar pages.
 * @param sidebarItem - The sidebar item configuration with page content.
 * @returns An object with file paths as keys and full slugs as values.
 */
export function createFilePathToSlugMap(
	sidebarItems: ParsedSidebarItemConfigWithPageContent[] | ParsedPageWithContent[],
): Record<string, string> {
	const map: Record<string, string> = {}

	function processItem(item: ParsedSidebarItemConfigWithPageContent | ParsedPageWithContent) {
		if ('filePath' in item && 'fullSlug' in item) {
			map[item.filePath] = item.fullSlug
		}

		if ('pages' in item && Array.isArray(item.pages)) {
			item.pages.forEach(processItem)
		}
	}

	sidebarItems.forEach(processItem)

	return map
}

/**
 * Removes anything before "/guides" from a given string.
 * If "/guides" is not found in the string, it returns the original string.
 *
 * @param path - The input string to process
 * @returns The processed string with everything before "/guides" removed
 */
export function removeBeforeGuides(path: string): string {
	const guidesIndex = path.indexOf('/guides')
	if (guidesIndex === -1) {
		return path
	}
	return path.slice(guidesIndex)
}
